import {
  FC,
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
  useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Loader2, Lock, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { NsKey } from 'enums/NsKey';
import { TagNoteDto } from 'api/note';
import { useNoteTags } from '../hooks/useNoteTags';

interface TagAutocompleteProps {
  selectedTags: TagNoteDto[];
  onTagsChange: (tags: TagNoteDto[]) => void;
}

/**
 * Tag autocomplete component with:
 * - Search and autocomplete from API
 * - Ability to create new tags
 * - Ability to remove any tag from note
 * - Ability to permanently delete non-global tags (with confirmation)
 */
const TagAutocomplete: FC<TagAutocompleteProps> = ({
  selectedTags,
  onTagsChange
}) => {
  const { t } = useTranslation(NsKey.Note);
  const {
    suggestions,
    isLoading,
    isCreating,
    isDeleting,
    searchTags,
    loadAllTags,
    createTag,
    canDeleteTagPermanently,
    deleteTagPermanently
  } = useNoteTags();

  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [tagToDelete, setTagToDelete] = useState<TagNoteDto | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue.trim()) {
        searchTags(inputValue);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, searchTags]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter out already selected tags from suggestions
  const filteredSuggestions = suggestions.filter(
    (suggestion) => !selectedTags.some((tag) => tag.id === suggestion.id)
  );

  // Check if input matches an existing tag exactly
  const exactMatch = suggestions.find(
    (s) => s.label?.toLowerCase() === inputValue.trim().toLowerCase()
  );

  // Check if we can create a new tag (no exact match found)
  const canCreateNew =
    inputValue.trim().length > 0 &&
    !exactMatch &&
    !selectedTags.some(
      (tag) => tag.label?.toLowerCase() === inputValue.trim().toLowerCase()
    );

  /**
   * Add an existing tag from suggestions
   */
  const handleSelectTag = useCallback(
    (tag: TagNoteDto) => {
      if (!selectedTags.some((t) => t.id === tag.id)) {
        onTagsChange([...selectedTags, tag]);
      }
      setInputValue('');
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    },
    [selectedTags, onTagsChange]
  );

  /**
   * Create and add a new tag
   */
  const handleCreateTag = useCallback(async () => {
    if (!inputValue.trim() || isCreating) return;

    const newTag = await createTag(inputValue.trim());
    if (newTag) {
      onTagsChange([...selectedTags, newTag]);
      setInputValue('');
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
    }
  }, [inputValue, isCreating, createTag, selectedTags, onTagsChange]);

  /**
   * Remove a tag from the note (always allowed, for any tag)
   */
  const handleRemoveTagFromNote = useCallback(
    (tag: TagNoteDto) => {
      onTagsChange(selectedTags.filter((t) => t.id !== tag.id));
    },
    [selectedTags, onTagsChange]
  );

  /**
   * Request to permanently delete a tag (show confirmation)
   */
  const handleRequestDeleteTag = useCallback(
    (e: React.MouseEvent, tag: TagNoteDto) => {
      e.stopPropagation();
      setTagToDelete(tag);
    },
    []
  );

  /**
   * Confirm permanent deletion of a tag
   */
  const handleConfirmDeleteTag = useCallback(async () => {
    if (!tagToDelete?.id) return;

    const success = await deleteTagPermanently(tagToDelete.id);
    if (success) {
      // Also remove from selected tags if present
      onTagsChange(selectedTags.filter((t) => t.id !== tagToDelete.id));
    }
    setTagToDelete(null);
  }, [tagToDelete, deleteTagPermanently, selectedTags, onTagsChange]);

  /**
   * Cancel deletion
   */
  const handleCancelDelete = useCallback(() => {
    setTagToDelete(null);
  }, []);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const totalItems = filteredSuggestions.length + (canCreateNew ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isDropdownOpen && inputValue) {
          setIsDropdownOpen(true);
        }
        setHighlightedIndex((prev) =>
          prev < totalItems - 1 ? prev + 1 : 0
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : totalItems - 1
        );
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex < filteredSuggestions.length) {
            handleSelectTag(filteredSuggestions[highlightedIndex]);
          } else if (canCreateNew) {
            handleCreateTag();
          }
        } else if (canCreateNew) {
          handleCreateTag();
        } else if (filteredSuggestions.length === 1) {
          handleSelectTag(filteredSuggestions[0]);
        }
        break;

      case 'Escape':
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        break;

      case 'Backspace':
        if (!inputValue && selectedTags.length > 0) {
          const lastTag = selectedTags[selectedTags.length - 1];
          handleRemoveTagFromNote(lastTag);
        }
        break;
    }
  };

  return (
    <div className="flex-1">
      <div className="flex flex-wrap items-center gap-2">
        {/* Selected tags */}
        {selectedTags.map((tag) => {
          const isGlobal = tag.isGlobal;

          return (
            <span
              key={tag.id}
              className={clsx(
                'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs',
                isGlobal
                  ? 'bg-primary/10 text-primary'
                  : 'bg-slate-100 text-slate-500'
              )}
              style={
                tag.color
                  ? {
                      backgroundColor: `${tag.color}15`,
                      color: tag.color
                    }
                  : undefined
              }
            >
              {tag.label}
              {isGlobal && <Lock size={10} className="ml-0.5 opacity-50" />}
              <button
                type="button"
                onClick={() => handleRemoveTagFromNote(tag)}
                className="text-current opacity-50 hover:opacity-100"
              >
                <X size={12} />
              </button>
            </span>
          );
        })}

        {/* Input with autocomplete */}
        <div className="relative">
          <div className="flex items-center gap-1">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                // Load all tags on focus to show default suggestions
                loadAllTags();
                setIsDropdownOpen(true);
              }}
              placeholder={t('FORM.TAGS_PLACEHOLDER')}
              className="h-7 px-2 rounded-md border border-slate-200 bg-white text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-primary transition-colors w-32"
            />
            {isLoading || isCreating ? (
              <div className="h-7 w-7 flex items-center justify-center">
                <Loader2 size={14} className="animate-spin text-slate-400" />
              </div>
            ) : (
              <button
                type="button"
                onClick={canCreateNew ? handleCreateTag : undefined}
                disabled={!canCreateNew}
                className="h-7 w-7 flex items-center justify-center rounded-md bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus size={14} />
              </button>
            )}
          </div>

          {/* Dropdown */}
          {isDropdownOpen &&
            (filteredSuggestions.length > 0 || canCreateNew) && (
              <div
                ref={dropdownRef}
                className="absolute top-full left-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto"
              >
                {/* Suggestions */}
                {filteredSuggestions.map((suggestion, index) => (
                  <div
                    key={suggestion.id}
                    className={clsx(
                      'flex items-center gap-2 px-3 py-2 text-xs',
                      highlightedIndex === index
                        ? 'bg-primary/5 text-primary'
                        : 'text-slate-600 hover:bg-slate-50'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelectTag(suggestion)}
                      className="flex items-center gap-2 flex-1 text-left"
                    >
                      {suggestion.color && (
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: suggestion.color }}
                        />
                      )}
                      <span className="flex-1 truncate">{suggestion.label}</span>
                      {suggestion.isGlobal && (
                        <Lock size={10} className="text-slate-400 flex-shrink-0" />
                      )}
                    </button>
                    {/* Delete button for non-global tags */}
                    {canDeleteTagPermanently(suggestion) && (
                      <button
                        type="button"
                        onClick={(e) => handleRequestDeleteTag(e, suggestion)}
                        className="p-1 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                        title={t('FORM.DELETE_TAG_PERMANENTLY')}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}

                {/* Create new option */}
                {canCreateNew && (
                  <button
                    type="button"
                    onClick={handleCreateTag}
                    className={clsx(
                      'w-full text-left px-3 py-2 text-xs flex items-center gap-2 border-t border-slate-100',
                      highlightedIndex === filteredSuggestions.length
                        ? 'bg-primary/5 text-primary'
                        : 'text-slate-600 hover:bg-slate-50'
                    )}
                  >
                    <Plus size={12} />
                    <span>
                      {t('FORM.CREATE_TAG', { name: inputValue.trim() })}
                    </span>
                  </button>
                )}
              </div>
            )}

          {/* Delete confirmation modal */}
          {tagToDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
              <div className="bg-white rounded-lg shadow-xl p-5 max-w-sm mx-4">
                <h3 className="text-sm font-semibold text-slate-800 mb-2">
                  {t('FORM.DELETE_TAG_CONFIRM_TITLE')}
                </h3>
                <p className="text-xs text-slate-600 mb-4">
                  {t('FORM.DELETE_TAG_CONFIRM_MESSAGE', {
                    name: tagToDelete.label
                  })}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    disabled={isDeleting}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors"
                  >
                    {t('COMMON.CANCEL')}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDeleteTag}
                    disabled={isDeleting}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded-md hover:bg-red-600 disabled:opacity-50 transition-colors flex items-center gap-1"
                  >
                    {isDeleting && (
                      <Loader2 size={12} className="animate-spin" />
                    )}
                    {t('COMMON.DELETE')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TagAutocomplete;
